// Reconnect schedule for the Phase 1 robustness pass on
// `deepgramStreamingEngine.ts`. See docs/planning/deepgram_streaming_robustness.md
// §3 Phase 1.
//
// Decisions baked in (locked defaults from §4):
//   - 5 attempts × ≤5s = ~25s total window before we give up and surface
//     "Reconnecting failed – tap mic to retry."
//   - Backoff = 500ms, 1s, 2s, 5s, 5s (cap at 5s for any further attempts).
//     `nextReconnectDelayMs(1)` returns 500; attempt index is 1-based to
//     match the user-facing "attempt N / 5" status string.

export const RECONNECT_DELAYS_MS: readonly number[] = [500, 1000, 2000, 5000, 5000];

export const MAX_RECONNECT_ATTEMPTS = RECONNECT_DELAYS_MS.length;

/**
 * Returns the wait time in ms before the Nth reconnect attempt (1-based).
 * Out-of-range attempts return the capped tail value (5000ms) — callers
 * are expected to bail before this matters, but the cap is defensive in
 * case some future caller loops past `MAX_RECONNECT_ATTEMPTS`.
 */
export function nextReconnectDelayMs(attempt: number): number {
  if (!Number.isFinite(attempt) || attempt < 1) return RECONNECT_DELAYS_MS[0];
  const idx = Math.min(Math.floor(attempt) - 1, RECONNECT_DELAYS_MS.length - 1);
  return RECONNECT_DELAYS_MS[idx];
}

/** Deepgram auth/token error close codes — always refetch before reconnect. Phase 2 §3. */
export const DEEPGRAM_AUTH_CLOSE_CODES = [4001, 4008, 4009] as const;

export type DeepgramAuthCloseCode = (typeof DEEPGRAM_AUTH_CLOSE_CODES)[number];

export function isDeepgramAuthCloseCode(code: number): code is DeepgramAuthCloseCode {
  return (DEEPGRAM_AUTH_CLOSE_CODES as readonly number[]).includes(code);
}

/**
 * Pick a token for reconnect: auth closes always refetch; other closes may
 * reuse the cached session token when still available.
 */
export async function resolveReconnectToken(args: {
  lastCloseCode: number | null;
  cachedToken: string | null;
  fetchToken: () => Promise<string>;
}): Promise<string> {
  if (args.lastCloseCode != null && isDeepgramAuthCloseCode(args.lastCloseCode)) {
    return args.fetchToken();
  }
  if (args.cachedToken) return args.cachedToken;
  return args.fetchToken();
}
